import { Button } from "@/components/ui/baseUi/button"
import { Chip } from "@/components/ui/baseUi/chip"
import { ChipGroup } from "@/components/ui/baseUi/chip-groupe"
import { Text } from "@/components/ui/baseUi/text"
import { withForm } from "@/components/ui/forms/hooks/useFormHook"
import type { CoachCategoryInfoFormFields } from "@modules/coachCategories/formTypes/coachCategoryFormTypes"
import { useCoachOffers } from "@modules/coachOffers/hooks/useCoachOffers"
import { word } from "@modules/core/utils/i18n"
import { useStore } from "@tanstack/react-store"
import { ChevronDown, ChevronUp } from "lucide-react"
import { useState } from "react"

export const CoachCategoryOfferInput = withForm({
  defaultValues: {} as CoachCategoryInfoFormFields,
  render: function CoachCategoryOfferInputInner({ form }) {
    const [areOffersExpanded, setAreOffersExpanded] = useState(false)
    const { data: coachOffers } = useCoachOffers()

    const offerIds = useStore(form.baseStore, (state) => state.values.offerIds)

    const onToggleOffer = (offerId: string) => {
      const index = offerIds.indexOf(offerId)
      if (index >= 0) {
        form.setFieldValue(
          "offerIds",
          offerIds.filter((id) => id !== offerId),
        )
      } else {
        form.setFieldValue("offerIds", [...offerIds, offerId])
      }
    }

    const onToggleAllOffers = () => {
      if (offerIds.length === coachOffers.length) {
        form.setFieldValue("offerIds", [])
      } else {
        form.setFieldValue(
          "offerIds",
          coachOffers.map((offer) => offer.id),
        )
      }
    }

    return (
      <div className="flex flex-col gap-2">
        <Text fontStyle="LM" className="font-semibold uppercase">
          {word("coach.planning.category.form.offers.label")}
        </Text>
        <Text fontStyle="LM" className="font-semibold">
          {word("coach.planning.category.form.offers.description")}
        </Text>

        <div className="rounded-xl border-2 border-surface-border-gray-default bg-surface-background-secondary p-4">
          <div className="flex items-center justify-between gap-4">
            <Text fontStyle="TS" className="text-textOnSurface-default">
              {word("coach.planning.category.form.offers.title")}
            </Text>
            <div className="flex flex-row items-center gap-2">
              <Text fontStyle="BS" className="text-textOnSurface-default">
                {`${offerIds.length.toString()}/${coachOffers.length.toString()} ${word("global.selected", {
                  count: offerIds.length === 0 ? 1 : offerIds.length,
                })}`}
              </Text>
              <Button variant="underline" size="xs" onClick={onToggleAllOffers}>
                {offerIds.length !== coachOffers.length
                  ? word("global.filters.selectAll")
                  : word("global.filters.unselectAll")}
              </Button>
            </div>
          </div>

          <ChipGroup multiple spacing={2} className="my-4 flex flex-wrap items-center">
            {coachOffers
              .filter((_, index) => (areOffersExpanded ? true : index < 4))
              .map((offer) => (
                <Chip
                  key={offer.id}
                  pressed={offerIds.includes(offer.id)}
                  onPressedChange={() => {
                    onToggleOffer(offer.id)
                  }}
                >
                  {offer.name}
                </Chip>
              ))}
          </ChipGroup>

          {coachOffers.length > 4 && (
            <div
              onClick={() => {
                setAreOffersExpanded((v) => !v)
              }}
              className="flex w-full cursor-pointer flex-col items-center justify-center gap-1 transition-all duration-150"
            >
              <Text fontStyle="BS">
                {areOffersExpanded
                  ? word("box.programmation.form.offers.showLess")
                  : word("box.programmation.form.offers.showMore")}
              </Text>
              {areOffersExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
            </div>
          )}
        </div>
      </div>
    )
  },
})
